import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useRealtimeEvent } from '@/context/NotificationContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Search,
  Eye,
  Hand,
  Play,
  Clock,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import ErpSyncBadge from '@/components/prescriptions/ErpSyncBadge';

const STATUS_VARIANTS = {
  PENDING: { label: 'Pending', className: 'bg-amber-100 text-amber-800 border-amber-300' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-800 border-blue-300' },
  WAITING: { label: 'Waiting', className: 'bg-purple-100 text-purple-800 border-purple-300' },
  COMPLETED: { label: 'Completed', className: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELLED: { label: 'Cancelled', className: 'bg-slate-200 text-slate-700 border-slate-300' }
};

export const UserPrescriptionList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('ALL'); // ALL, PENDING, IN_PROGRESS, WAITING, COMPLETED, UNASSIGNED
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);

  const fetchPrescriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        page_size: pageSize,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (activeTab === 'UNASSIGNED') {
        params.unassigned = true;
        params.unassigned_only = true;
      } else if (activeTab !== 'ALL') {
        params.workflow_status = activeTab;
        params.status = activeTab;
      }

      const res = await api.get('/prescriptions', { params });
      if (res.data) {
        setPrescriptions(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load prescriptions:', err);
      toast.error('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, activeTab]);

  useEffect(() => {
    fetchPrescriptions();
  }, [fetchPrescriptions]);

  // Real-time automatic updates (zero manual refresh needed)
  useRealtimeEvent(
    [
      'prescription.created',
      'prescription.assigned',
      'prescription.updated',
      'prescription.status_changed',
      'prescription.note_added',
    ],
    useCallback(() => {
      fetchPrescriptions();
    }, [fetchPrescriptions])
  );

  const handleClaim = async (id) => {
    setClaimingId(id);
    try {
      await api.post(`/prescriptions/${id}/claim`);
      toast.success('Prescription claimed successfully');
      fetchPrescriptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not claim prescription');
    } finally {
      setClaimingId(null);
    }
  };

  const handleStart = async (id) => {
    try {
      await api.post(`/prescriptions/${id}/status`, { status: 'IN_PROGRESS' });
      toast.success('Started processing');
      navigate(`/user/prescriptions/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  const STATUS_TABS = [
    { key: 'ALL', label: 'All My Work' },
    { key: 'PENDING', label: 'Pending', icon: Clock },
    { key: 'IN_PROGRESS', label: 'In Progress', icon: Play },
    { key: 'WAITING', label: 'Waiting', icon: AlertCircle },
    { key: 'COMPLETED', label: 'Completed', icon: CheckCircle2 },
    { key: 'UNASSIGNED', label: 'Unassigned Pool', icon: Hand }
  ];

  return (
    <div className="space-y-6 pb-8 md:pb-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-start justify-between gap-3 w-full sm:w-auto">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Prescription Operations
            </h1>
            <p className="text-slate-600 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Fulfill and manage patient prescriptions assigned to your desk
            </p>
          </div>
          {/* Mobile Icon-only Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchPrescriptions}
            disabled={loading}
            className="sm:hidden h-9 w-9 flex-shrink-0"
            aria-label="Refresh prescriptions"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Desktop Refresh Button */}
        <Button
          variant="outline"
          onClick={fetchPrescriptions}
          disabled={loading}
          className="hidden sm:inline-flex gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Desktop Tabs (md+) */}
      <div className="hidden md:flex flex-wrap gap-2 border-b pb-3">
        {STATUS_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <Button
              key={tab.key}
              variant={isActive ? 'default' : 'ghost'}
              size="sm"
              onClick={() => { setActiveTab(tab.key); setPage(1); }}
              className={`filter-strip-item gap-1.5 h-9 whitespace-nowrap ${isActive ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'text-slate-600'}`}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {tab.label}
            </Button>
          );
        })}
      </div>

      {/* Mobile Filter Dropdown (< md) */}
      <div className="block md:hidden">
        <label htmlFor="user-rx-status-filter" className="block text-xs font-semibold text-slate-700 mb-1.5">
          Filter by Status
        </label>
        <div className="relative">
          <select
            id="user-rx-status-filter"
            value={activeTab}
            onChange={(e) => {
              setActiveTab(e.target.value);
              setPage(1);
            }}
            className="w-full h-11 pl-3.5 pr-9 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 appearance-none"
          >
            {STATUS_TABS.map((tab) => (
              <option key={tab.key} value={tab.key}>
                {tab.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-500">
            <Filter className="h-4 w-4" />
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="shadow-sm border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by patient name, phone, or prescription ID..."
              className="pl-9 h-10 border-slate-300"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table & Mobile Cards */}
      <Card className="shadow-sm border-slate-200">
        <CardHeader className="border-b border-slate-100 py-4 px-4 sm:px-6 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold text-slate-800">
            Prescriptions ({total})
          </CardTitle>
          <span className="text-xs text-slate-500">
            Page {page} of {totalPages}
          </span>
        </CardHeader>
        <CardContent className="p-0">
          {/* ── MOBILE card list (< md) ── */}
          <div className="block md:hidden divide-y divide-slate-100">
            {loading ? (
              <div className="text-center py-12 text-slate-500">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto text-emerald-600 mb-2" />
                Loading prescriptions...
              </div>
            ) : prescriptions.length > 0 ? (
              prescriptions.map((rx) => {
                const statusMeta = STATUS_VARIANTS[rx.workflow_status] || {
                  label: rx.workflow_status || 'Pending',
                  className: 'bg-slate-100 text-slate-700'
                };
                const isUnassigned = !rx.assigned_user_id;
                return (
                  <div
                    key={rx.id}
                    className="p-4 space-y-3 cursor-pointer hover:bg-slate-50/50"
                    onClick={() => navigate(`/user/prescriptions/${rx.id}`)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-sm truncate text-slate-900">
                            {rx.patient_name || rx.patientName || 'Patient'}
                          </p>
                          <span className="font-mono text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded flex-shrink-0">
                            #{rx.id}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {rx.visit_date || rx.date || '—'} • {rx.type || 'Digital'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
                        <Badge className={`text-xs font-medium border ${statusMeta.className}`}>
                          {statusMeta.label}
                        </Badge>
                        <ErpSyncBadge status={rx.erp_sync_status} invoiceNo={rx.erp_invoice_no} />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/user/prescriptions/${rx.id}`);
                          }}
                          aria-label="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {(isUnassigned || rx.workflow_status === 'PENDING') && (
                      <div className="pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                        {isUnassigned && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full h-9 text-xs gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50 justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClaim(rx.id);
                            }}
                            disabled={claimingId === rx.id}
                          >
                            <Hand className="h-3.5 w-3.5" />
                            {claimingId === rx.id ? 'Claiming...' : 'Claim Prescription'}
                          </Button>
                        )}
                        {!isUnassigned && rx.workflow_status === 'PENDING' && (
                          <Button
                            size="sm"
                            className="w-full h-9 text-xs gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStart(rx.id);
                            }}
                          >
                            <Play className="h-3.5 w-3.5" /> Start Processing
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 text-slate-500 text-sm">
                No prescriptions found in this view.
              </div>
            )}
          </div>

          {/* ── DESKTOP table (md+) ── */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Visit Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>ERP Sync</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto text-emerald-600 mb-2" />
                      Loading prescriptions...
                    </TableCell>
                  </TableRow>
                ) : prescriptions.length > 0 ? (
                  prescriptions.map((rx) => {
                    const statusMeta = STATUS_VARIANTS[rx.workflow_status] || {
                      label: rx.workflow_status || 'Pending',
                      className: 'bg-slate-100 text-slate-700'
                    };
                    const isUnassigned = !rx.assigned_user_id;

                    return (
                      <TableRow key={rx.id} className="hover:bg-slate-50/75">
                        <TableCell className="font-mono text-xs text-slate-500">#{rx.id}</TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-900">{rx.patient_name || rx.patientName || 'Patient'}</div>
                          {rx.phone && <div className="text-xs text-slate-500">{rx.phone}</div>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{rx.visit_date || rx.date || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs font-normal">{rx.type || 'Digital'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs font-medium border ${statusMeta.className}`}>{statusMeta.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <ErpSyncBadge status={rx.erp_sync_status} invoiceNo={rx.erp_invoice_no} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isUnassigned && (
                              <Button size="sm" variant="outline" className="h-8 gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                                onClick={() => handleClaim(rx.id)} disabled={claimingId === rx.id}>
                                <Hand className="h-3.5 w-3.5" />
                                {claimingId === rx.id ? 'Claiming...' : 'Claim'}
                              </Button>
                            )}
                            {!isUnassigned && rx.workflow_status === 'PENDING' && (
                              <Button size="sm" className="h-8 gap-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                                onClick={() => handleStart(rx.id)}>
                                <Play className="h-3.5 w-3.5" /> Start
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-8 gap-1 text-slate-600 hover:text-slate-900"
                              onClick={() => navigate(`/user/prescriptions/${rx.id}`)}>
                              <Eye className="h-3.5 w-3.5" /> Details
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      No prescriptions found in this view.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
              <span className="text-xs text-slate-600">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserPrescriptionList;
